import { ViewerBackend } from './ViewerBackend';
import { veCall, getCloudApiBase } from './veCall';
import {
  classifyDevice,
  buildCloudLiveUrl,
  buildCloudClipUrl,
  buildCloudThumbUrl,
  buildCloudLegacyClipUrl,
  canAttachCloudToken,
} from './cloudUrls';
import {
  mapLoginPayload,
  defaultActiveDeviceId,
  deviceToJumpTarget,
  deviceCamerasToGrid,
  allDevicesToGrid,
  sessionViewsToSlotRows,
  layoutToViewCams,
  saveCloudSession,
  loadCloudSession,
  clearCloudSession,
} from './cloudSession';
import { localToUtc, utcToLocal, tzOffsetString } from '../util/cloudTime';
import { dayDots, monthKey } from '../util/mcpbPresence';

// Compact error text for a failed veCall — the HTTP status when present (401/403/502…),
// else the transport message. Used by the discovery methods, which resolve (never throw)
// so the Setup UI can surface a message without a try/catch at every call site.
const cloudErr = (e) => {
  const s = e && e.response && e.response.status;
  return s ? `HTTP ${s}` : (e && e.message) || 'request failed';
};

/**
 * CloudNvrAdapter — ViewerBackend over cloudapi's `/ve` REST + JWT (the cloud
 * NVR that replaces the isite portal at nvr.cloud). It is the cloud sibling of
 * LocalNvrAdapter: same interface, different transport.
 *
 * Session model: `/ve/login` returns a rich payload (user, devices, cameras,
 * views, org). The adapter normalizes it ONCE into a cloudSession (see
 * cloudSession.js) and holds it — the whole payload is reused, not dropped. The
 * UI reads it through accessors (getJumpTargets / getGridCameras / getUser /
 * getViews). `getBackend()` caches one cloud adapter, so the session persists
 * across calls for the app session (Redux rehydrate on reload is a later step).
 *
 * Jump = ACTIVE DEVICE. One JWT authorizes every device in its `devices` claim,
 * so "jumping" is just switching which device's cameras the grid shows — a
 * device-id change in the stream URLs, no re-auth (unlike the local NVR jump,
 * which opens a new per-server session). `getGridCameras()` returns the active
 * device's cameras; `setActiveDevice()` switches it.
 *
 * LIVE is uniform for every cloud device (VERIFIED vs prod 2026-07-16): dragon
 * and legacy alike stream raw H.264 over a token-authed WebSocket off
 * engine_backend, feeding the one WebCodecs player (PlayerWS). So buildLiveUrl
 * has a single path — no `if (dragon)` at the player. The dragon-vs-regular
 * capability split (cloudUrls.classifyDevice) still applies to PLAYBACK/CONFIG
 * (Day-3): dragon playback is a byte-range MP4 off engine_backend; the regular
 * field-fleet playback (pbctrl-cached) plus streamStart stay reachable.
 */
// A ScaleWatcher enrollment refusal as the Setup row shows it: the server's own reason, else plain
// words for the transport, never axios's raw text (review L10). `step` names what failed.
const swEnrollmentError = (e, step) => {
  const d = e && e.response && e.response.data;
  if (d && typeof d.error === 'string' && d.error) return d.error;
  const status = e && e.response && e.response.status;
  if (status === 401) return 'Your session ended. Sign in again.';
  if (status) return `${step} Try again.`;
  return "Couldn't reach Dividia Cloud. Check the connection and try again.";
};

// What a session may see beyond its device list: its cameras (per-camera ACL) and per-device rights.
// Names, online flags and views are not access, so they never count as a change. The rights map
// arrives keyed in the server's device order (by name), so it is compared key-sorted: a rename that
// only reorders it is no change (review L7).
const sortedEntries = (map) => Object.keys(map).sort()
  .map((key) => [key, map[key] && typeof map[key] === 'object' ? sortedEntries(map[key]) : map[key]]);
const accessKey = (session) => JSON.stringify(session ? [
  (session.cameras || []).map((c) => `${c.sDeviceID}:${c.bCamera}:${c.bProfile}`).sort(),
  session.deviceRights ? sortedEntries(session.deviceRights) : null,
] : null);

export class CloudNvrAdapter extends ViewerBackend {
  constructor() {
    super();
    /** @type {string|null} JWT bearer, set on login(). */
    this._token = null;
    /** @type {string|null} RS256 refresh token, portal Microsoft OAuth only. Lets the
     *  cloud viewer re-mint the short 1-hour cloud JWT via /ve/session/refresh so a
     *  Microsoft revocation logs the user out. null for password login. */
    this._refreshToken = null;
    /** @type {Object|null} normalized /ve/login model (see cloudSession). */
    this._session = null;
    /** @type {string|null} device whose cameras the grid shows (the jump target). */
    this._activeDeviceId = null;
    /** @type {Object<string, boolean[]>} recording-day presence dots cached per `${device}|${cam}|YYYYMM`
     *  (card 86bbqvr6f) — paging months in a fixed view is free; a grid change fetches only the
     *  newly-added cameras. Cleared on logout/expiry (clearToken) so it can't cross accounts. */
    this._presenceCache = {};
    /** @type {function():Promise<boolean>|null} the app's access refresh (setAccessRefresher). */
    this._accessRefresher = null;
    // Rehydrate a persisted session so a page reload (fresh singleton) keeps working:
    // without this, _session is null → dragon misclassifies as legacy → clip URLs break
    // and the grid empties. Token + session + active device are restored together.
    this._rehydrate();
  }

  /** Restore token/session/activeDevice from localStorage after a page refresh. */
  _rehydrate() {
    const saved = loadCloudSession();
    if (!saved) return;
    this._session = saved.session;
    this._token = saved.token || null;
    this._refreshToken = saved.refreshToken || null;
    this._activeDeviceId = saved.activeDeviceId || defaultActiveDeviceId(saved.session);
  }

  /** Persist the current session so it survives a refresh. */
  _persist() {
    saveCloudSession({ token: this._token, refreshToken: this._refreshToken, session: this._session, activeDeviceId: this._activeDeviceId });
  }

  /** The portal refresh token, or null (password login has none). */
  getRefreshToken() {
    return this._refreshToken;
  }

  /** Store the portal refresh token and persist it (portal Microsoft OAuth login). */
  setRefreshToken(refreshToken) {
    this._refreshToken = refreshToken || null;
    this._persist();
  }

  /** Swap in a freshly refreshed cloud JWT (from /ve/session/refresh) and persist. */
  updateToken(token) {
    this._token = token || null;
    this._persist();
  }

  get kind() {
    return 'cloud';
  }

  // ---- token / session ---------------------------------------------------

  /** Inject a JWT (e.g. rehydrated from Redux on reload). */
  setToken(token) {
    this._token = token || null;
  }

  /** Clear session state on logout (in-memory AND the persisted refresh copy). */
  clearToken() {
    this._token = null;
    this._refreshToken = null;
    this._session = null;
    this._activeDeviceId = null;
    this._presenceCache = {};
    clearCloudSession();
  }

  // ---- auth --------------------------------------------------------------

  /**
   * POST /ve/loginV2 → the rich payload. Normalizes it into the cloud session
   * (devices, cameras-by-device, views, user, org), stashes the JWT, and picks
   * the default active device (first with cameras). Returns the raw payload.
   * @param {{username:string, password:string}} creds
   * @param {Object} [opts] - { timeout }
   * @returns {Promise<Object>} the login payload (with .token)
   */
  async login({ username, password } = {}, opts = {}) {
    // Cloud-lite logs in via /ve/loginV2 — the endpoint that ALWAYS returns a per-camera-ACL-enforced
    // session + acl:1 token. The legacy /ve/login stays device-level for the native desktop/mobile viewers
    // (dview-desktop, MacView). Enforcement is decided by the ENDPOINT (a server-side fact), not a client
    // signal that an out-of-browser caller could omit. (card 86bbqvr65)
    const res = await veCall('loginV2', {
      method: 'post',
      data: { username, password },
      timeout: opts.timeout ?? 15000,
    });
    return this.establishSession(res?.data || {});
  }

  /**
   * Ingest an already-fetched /ve/login-shaped payload into the cloud session
   * (the after-HTTP body of login()). The portal Microsoft OAuth path
   * (AuthActions.loginCloudMicrosoft) receives the same payload from the status
   * poll and calls this directly — password login runs it internally, so password
   * ingestion is unchanged and happens exactly once.
   * @param {Object} payload - the /ve/login response (with .token, .cameras, .user, ...)
   * @returns {Object} the same payload
   */
  establishSession(payload = {}) {
    this._session = mapLoginPayload(payload);
    this._token = this._session.token;
    this._activeDeviceId = defaultActiveDeviceId(this._session);
    // A new session is a (possibly different) identity — drop any presence dots cached under the prior
    // session so a re-login on this singleton adapter can't serve account A's recording-days to account B
    // for a device both can see (card 86bbqvr6f).
    this._presenceCache = {};
    this._persist();   // survive a page refresh (see _rehydrate)
    return payload;
  }

  /**
   * Re-read the session's access from the server (#86bc6dkfa; cloudapi POST /ve/session/access,
   * authenticated by the current token alone) and adopt it (applySession).
   * @returns {Promise<{changed:boolean, removed:Object[], added:Object[], payload:Object}>}
   */
  async refreshAccess() {
    const sent = this._token;
    const sentUser = this._session && this._session.user ? this._session.user.bID : null;
    const hadSession = Boolean(this._session);
    // Signed out, signed in again, or re-minted by the portal refresh (which carried current access)
    // while this was out: the reply is for a session that is gone (review L3). So is a refusal: a late
    // 401 for the old token must not end the session that replaced it (#86bc7maf2). Two refusals still
    // count: any, when there was no session to begin with (a stale sign-in should end), and SESSION_ENDED
    // for the same user (the account itself was disabled or deleted, whatever token it answered).
    const gone = () => !this._session || this._token !== sent;
    const unchanged = { changed: false, removed: [], added: [], payload: {} };
    let res;
    try {
      res = await veCall('session/access', { method: 'post', token: sent, timeout: 15000 });
    } catch (error) {
      const data = (error && error.response && error.response.data) || {};
      const accountEnded = data.code === 'SESSION_ENDED' && sentUser != null
        && Boolean(this._session && this._session.user && this._session.user.bID === sentUser);
      if (hadSession && gone() && !accountEnded) return unchanged;
      throw error;
    }
    if (gone()) return unchanged;
    return this.applySession((res && res.data) || {});
  }

  /**
   * Adopt a refreshed /ve login payload without resetting what the viewer shows (#86bc6dkfa): the
   * active device and presence dots stay while their device does. `removed` holds the OLD session's
   * device objects, so callers can name them. When access is unchanged nothing is replaced, the
   * token included, because live stream URLs carry it and would restart; `takeToken` still adopts a
   * new token (the portal's 1-hour JWT must roll every refresh).
   * @returns {{changed:boolean, removed:Object[], added:Object[], payload:Object}}
   */
  applySession(payload = {}, { takeToken = false } = {}) {
    const next = mapLoginPayload(payload);
    const prev = this._session;
    const removed = ((prev && prev.devices) || []).filter((d) => !next.devicesById[d.id]);
    const added = next.devices.filter((d) => !(prev && prev.devicesById && prev.devicesById[d.id]));
    const changed = removed.length > 0 || added.length > 0 || accessKey(prev) !== accessKey(next);
    if (!changed) {
      if (takeToken && next.token && next.token !== this._token) {
        this._token = next.token;
        if (this._session) this._session = { ...this._session, token: next.token };
        this._persist();
      }
      return { changed: false, removed: [], added: [], payload };
    }
    this._session = next;
    this._token = next.token;
    if (!next.devicesById[this._activeDeviceId]) this._activeDeviceId = defaultActiveDeviceId(next);
    for (const key of Object.keys(this._presenceCache)) {
      if (!next.devicesById[key.split('|')[0]]) delete this._presenceCache[key];
    }
    this._persist();
    return { changed: true, removed, added, payload };
  }

  /** Best-effort logout — cloud sessions are stateless JWTs, so just drop it. */
  async logout() {
    this.clearToken();
  }

  // ---- jump = active device ----------------------------------------------

  /**
   * The devices this user can view, shaped for the cloud Jump selector (id,
   * name, online, siteLabel, isActive). The cloud analog of the local NVR's
   * authServers list — one JWT covers them all, so selecting one is a device
   * switch, not a re-login.
   * @returns {Array<{id:string,name:string,online:boolean,siteLabel:string,isActive:boolean}>}
   */
  getJumpTargets() {
    if (!this._session) return [];
    return this._session.devices.map((d) => deviceToJumpTarget(d, this._activeDeviceId));
  }

  /** The active device id (which device's cameras the grid shows). */
  getActiveDeviceId() {
    return this._activeDeviceId;
  }

  /** The active device record, or null. */
  getActiveDevice() {
    return this._session ? this._session.devicesById[this._activeDeviceId] || null : null;
  }

  /**
   * Switch the active device (the "jump"). No re-auth — the JWT already covers
   * it; the caller re-reads getGridCameras() to repopulate the grid.
   * @param {string} deviceId
   * @returns {boolean} true if the device exists and is now active
   */
  setActiveDevice(deviceId) {
    if (this._session && this._session.devicesById[deviceId]) {
      this._activeDeviceId = deviceId;
      this._persist();   // remember the jumped-to device across a refresh
      return true;
    }
    return false;
  }

  /** The logged-in user record from the login payload (for the header/identity). */
  getUser() {
    return this._session ? this._session.user : null;
  }

  /** Per-device rights map from the login payload ({ sDeviceID → flags }), or null if absent. */
  getDeviceRights() {
    return this._session && this._session.deviceRights ? this._session.deviceRights : null;
  }

  /** sDeviceIDs of the session's devices in site (project) `projectId` — for site admin coverage. */
  getProjectDeviceIds(projectId) {
    if (!this._session) return [];
    return this._session.devices.filter((d) => d.projectId != null && Number(d.projectId) === Number(projectId)).map((d) => d.id);
  }

  /** Saved views from the login payload (for the view selector). */
  getViews() {
    return this._session ? this._session.views : [];
  }

  /**
   * The cloudapi backend version from the /ve/login payload (for the header). Sync
   * (getCapabilities() is async) and read straight from the persisted session, so it
   * survives a refresh with the rest of the cloud session. null pre-login / local.
   */
  getBackendVersion() {
    return this._session ? (this._session.backendVersion || null) : null;
  }

  /**
   * Follow the cloudapi build actually answering (veCall's X-Cloudapi-Version),
   * and persist it so a refresh restores the current version, not the login's.
   * No-op without a session: the login payload sets it.
   * @returns {boolean} whether the version changed
   */
  setBackendVersion(version) {
    if (!this._session || !version || this._session.backendVersion === version) return false;
    this._session = { ...this._session, backendVersion: version };
    this._persist();
    return true;
  }

  /**
   * Human label for the whole cloud account, for the header breadcrumb + ⓘ.
   * David's #86bb5dx62 decision: "Customer — Project". The all-devices grid has no
   * single active device, so this is ACCOUNT-level: the customer name, plus the
   * project/site ONLY when every device is at one site; degrade to just the customer
   * across multiple sites, and to "Dividia Cloud" when the account spans customers
   * or nothing is set. (`session.devices` are normalized with customerName/projectName.)
   * @returns {string}
   */
  getSystemLabel() {
    if (!this._session) return '';
    const devs = this._session.devices || [];
    const customers = [...new Set(devs.map((d) => d.customerName).filter(Boolean))];
    if (customers.length !== 1) return 'Dividia Cloud';
    const projects = [...new Set(devs.map((d) => d.projectName).filter(Boolean))];
    const label = [customers[0], projects.length === 1 ? projects[0] : ''].filter(Boolean).join(' — ');
    return label || 'Dividia Cloud';
  }

  // ---- grid --------------------------------------------------------------

  /**
   * Grid-ready cameras for the ACTIVE device only — the cloud analog of the
   * local NVR's post-login camera bootstrap. Every cloud device streams H.264
   * over the token-authed WS (the one WebCodecs player), so there is no
   * per-camera transport tag; `deviceOnline` is carried so the grid can show a
   * clean placeholder for an offline device instead of a dead WS. Switching
   * devices (setActiveDevice) + re-reading this is the whole "jump".
   * @returns {Array<Object>} cameras shaped for the live grid
   */
  getGridCameras() {
    if (!this._session || !this._activeDeviceId) return [];
    const device = this._session.devicesById[this._activeDeviceId];
    const cams = this._session.camerasByDeviceId[this._activeDeviceId] || [];
    return deviceCamerasToGrid(cams, device);
  }

  /**
   * ALL accessible devices' cameras, as the cross-device grid + camera pool the view
   * machinery runs against (the cloud analog of eView's `state.server.allCameras`).
   * Each camera carries the synthetic `bSerial = device.bID + 9999` and `bID = bCamera`
   * so the shared selectView/save/getViews logic resolves it unchanged; the cloud
   * "all-devices grid" decision means a saved view can span devices. Unlike
   * getGridCameras (one active device — the jump model being retired), this surfaces
   * every device at once. Empty before login.
   * @returns {Array<Object>} cameras for every device
   */
  getAllCameras() {
    return allDevicesToGrid(this._session);
  }

  // ---- views (cross-device; drives the shared view machinery) -------------
  // Cloud CRUD is confined to the adapter: reads map the login session's cloud views
  // (sCams) into the local getSets row shape; writes convert the grid layout back to
  // sCams and POST /ve/view. The view components (ViewActions/selectView/ViewReducer/
  // ViewDropdown) stay unchanged — cloud-ness lives here, in the shape mapping +
  // deviceId↔serial offset.

  /**
   * Any device id the user is authorized for. The `/ve/view` (+ `/ve/views`) routes are
   * guarded by cloudapi's `authDevices` middleware, which requires a `?device=<authorizedId>`
   * query param and 403s ('Access denied') when the token's device list doesn't contain it
   * — even though a VIEW is NOT device-specific (it spans devices) and modifyOrCreateView
   * ignores req.query.device entirely. So we satisfy the guard with any device from the
   * session (same as /ve/playbackevents + /ve/flagevent, which are also authDevices-guarded).
   */
  _anyDeviceId() {
    const d = this._session && this._session.devices;
    return d && d.length ? d[0].id : undefined;
  }

  /**
   * A device-scoped call. The /ve routes behind authDevices need ?device= to be a device in the
   * token (the controllers ignore it), so it is any session device unless the caller names one.
   * A 403 can mean the session's devices changed since sign-in (#86bc6dkfa): the app's access
   * refresh runs once (setAccessRefresher) and the call is retried with the refreshed session's
   * device and token.
   */
  async _deviceCall(path, { params = {}, token, ...rest } = {}) {
    const send = (bearer) => veCall(path, {
      ...rest,
      params: { ...params, device: params.device || this._anyDeviceId() },
      token: bearer,
    });
    try {
      return await send(token || this._token);
    } catch (error) {
      const forbidden = error && error.response && error.response.status === 403;
      if (!forbidden || !this._accessRefresher || !(await this._accessRefresher())) throw error;
      return send(this._token);
    }
  }

  /** The app's access refresh (AuthActions.refreshCloudAccess), resolving true when access changed. */
  setAccessRefresher(refresher) {
    this._accessRefresher = typeof refresher === 'function' ? refresher : null;
  }

  /**
   * The session's cloud views flattened into `config.user.getSets`-shaped rows
   * (`{sName, ixSlot, bSerial, bCamera, sFisheye, bUser, fDefault}`), so the getViews
   * thunk parses them with its existing (eView) body — bSerial = device.bID + 9999
   * routes each slot to the right device. The cloud realization of "getViews builds
   * viewSlots from sCams". Empty before login.
   * @returns {Array<Object>}
   */
  getViewSlotRows() {
    return sessionViewsToSlotRows(this._session);
  }

  /**
   * Create or update a cloud view in the NORMALIZED schema (JSView header + JSViewCam
   * slots). Converts the current grid layout to the cloud `cams` array (layoutToViewCams —
   * enabled, device-bearing cells, gaps preserved via `slot`) and POSTs `/ve/view`:
   * create = `{name, cams, bUserID, fDefault, fDividia}`, update adds `id`. The scope
   * flags drive the backend role gate (unified-viewer-cloud-views.md §6): `bUserID` 0 =
   * customer-global (admin only), else the owning user; `fDefault` = the user's default for
   * the device(s) it covers (§7); `fDividia` = Dividia-internal (bRole=1 only). The backend
   * is the actual gate — it re-derives the caller from the trusted token and 403s a
   * disallowed scope, so a crafted request can't escalate. On success the in-memory session
   * views are kept in sync (upsert) so a getViews() refresh reflects the change without a
   * re-login. (No `customerId`/`addToUser` — bCustomerID and the JSUser.sViews link are
   * both gone; delivery is device-access-driven, §4.)
   * @param {{id?:number, name:string, cameras:Array<Object>, bUserID?:number,
   *   fDefault?:boolean, fDividia?:boolean}} opts
   * @returns {Promise<{ok:boolean, id:?number}>}
   */
  async saveView({ id, name, cameras, bUserID, fDefault, fDividia } = {}) {
    if (!name || !String(name).trim()) throw new Error('View name is required');
    const cams = layoutToViewCams(cameras || []);
    const self = this._session && this._session.user ? this._session.user.bID : 0;
    const owner = bUserID != null ? Number(bUserID) : self;   // 0 = global · else = owner
    const body = {
      name,
      cams,                                        // [{deviceId,cam,profile,slot,fisheye}]
      bUserID: owner,
      fDefault: fDefault ? 1 : 0,
      fDividia: fDividia ? 1 : 0,
    };
    if (id != null) body.id = id;
    const res = await this._deviceCall('view', { method: 'post', data: body });
    const ok = !!(res && res.data && res.data.success);
    let newId = id;
    if (ok) {
      if (newId == null) {
        const data = res.data.data;                       // INSERT → [insertId]
        newId = Array.isArray(data) ? data[0] : data;
      }
      this._upsertSessionView({ id: newId, label: name, cams, bUserID: owner, fDefault: body.fDefault, fDividia: body.fDividia });
      this._persist();
    }
    return { ok, id: newId };
  }

  /**
   * Delete a cloud view by its JSView id → POST `/ve/view {id, del:1}` (also unlinks it
   * from every user's JSUser.sViews server-side). Keeps the in-memory session views in
   * sync on success. The cloud analog of `config.user.delSet`.
   * @param {number} id
   * @returns {Promise<{ok:boolean}>}
   */
  async deleteView(id) {
    const res = await this._deviceCall('view', { method: 'post', data: { id, del: 1 } });
    const ok = !!(res && res.data && res.data.success);
    if (ok) {
      this._removeSessionView(id);
      this._persist();
    }
    return { ok };
  }

  /**
   * Insert-or-replace a view in the in-memory session (post save), matched by id.
   * Immutable — builds a NEW array rather than mutating in place, because
   * mapLoginPayload aliases the raw `/ve/login` response's userViews array; mutating
   * it would corrupt the original response object (and, in tests, a shared fixture).
   */
  _upsertSessionView(view) {
    if (!this._session) return;
    const views = Array.isArray(this._session.views) ? this._session.views : [];
    const i = views.findIndex((v) => v && v.id === view.id);
    const next = views.slice();
    if (i >= 0) next[i] = { ...next[i], ...view };
    else next.push(view);
    this._session.views = next;
  }

  /** Drop a view from the in-memory session (post delete), matched by id (immutable). */
  _removeSessionView(id) {
    if (!this._session || !Array.isArray(this._session.views)) return;
    this._session.views = this._session.views.filter((v) => !(v && v.id === id));
  }

  // ---- setup: users (admin) ----------------------------------------------
  // Cloud user administration over /ve. Each route is authDevices-guarded, so they carry
  // ?device=_anyDeviceId() (the guard needs it; the controllers ignore it — same as the view
  // CRUD). The BACKEND is the real gate: it re-derives the caller from the trusted token,
  // enforces the role/scope gate (Phase 1) and writes bRights (Phase 2). The UI mirrors
  // (hides/disables) but never authorizes. Callers pass opts.token = Redux state.auth.sSess
  // (reliable across a code reload) falling back to the adapter's in-memory token.

  /** The logged-in user's bID — the ?id= fallback for older tokens (backend prefers the token bID). */
  _selfId() {
    return this._session && this._session.user ? this._session.user.bID : undefined;
  }

  /**
   * GET /ve/users → the whole Users-screen payload in ONE call. Returns
   * { users, customers, projects, devices, cameras, views }. Each user carries
   * bID/sName/sDescription/bRole/bRights/bCustomerID/sCustomerName/fDisabled, the legacy per-site
   * `access` (JSAcl), AND the authoritative per-device `grants:[{sDeviceID,bRights,bCameras(String)}]`.
   * `devices` (with org labels) is the AccessTree finder source; `cameras` (per-device rows) feeds the
   * per-device camera grid — so the drill UI needs no further fetches. bCameras is a decimal STRING
   * (BIGINT UNSIGNED, up to bit 63 → beyond JS's safe integer). Password hashes are never returned
   * (backend explicit-column select). Empty-safe.
   * @returns {Promise<{users:Array,customers:Array,projects:Array,devices:Array,cameras:Array,views:Array}>}
   */
  async getUsers(opts = {}) {
    const res = await this._deviceCall('users', {
      params: { id: this._selfId() },
      token: opts.token,
    });
    const d = (res && res.data) || {};
    return {
      users: d.users || [],
      customers: d.customers || [],
      projects: d.projects || [],
      devices: d.devices || [],
      cameras: d.cameras || [],
      views: d.views || [],
    };
  }

  /**
   * Create or update a user → POST /ve/user (gated). Body:
   * { id?, email, description, role, grants (JSON), views, password?|generatePassword?, sendCredentials? }.
   * `grants` is the AUTHORITATIVE per-device access definition — a JSON array of
   * {sDeviceID, bRights (0..63 int), bCameras (decimal String)} — and REPLACES the old global
   * `rights` + per-site `access`. `role` is the 0/1 master bit (only a master may send 1). We do NOT
   * send `customerId`: the backend derives it from the first granted device (the user↔customer tie is
   * being dropped). On create, omit id. Role/scope + per-device rights/cameras are enforced
   * server-side (subset-checked for a non-master admin); a crafted request can't escalate. Returns {ok,id}.
   * @param {{id?:number, email:string, description?:string, role?:number,
   *   grants?:Array<{sDeviceID:string,bRights:number,bCameras:string}>, views?:string,
   *   password?:string, generatePassword?:boolean, sendCredentials?:boolean}} user
   * @returns {Promise<{ok:boolean,id:?number}>}
   */
  async saveUser(user = {}, opts = {}) {
    const body = {
      email: user.email,
      description: user.description || '',
      role: user.role,
      grants: JSON.stringify(user.grants || []),
      views: user.views != null ? user.views : '[]',
    };
    if (user.id != null) body.id = user.id;                 // update (else create)
    if (user.generatePassword) body.generatePassword = 1;
    else if (user.password) body.password = user.password;
    if (user.sendCredentials) body.sendCredentials = true;
    const res = await this._deviceCall('user', { method: 'post', data: body, token: opts.token });
    const ok = !!(res && res.data && res.data.success);
    const data = res && res.data && res.data.data;          // INSERT → [insertId]
    const id = user.id != null ? user.id : (Array.isArray(data) ? data[0] : data);
    return { ok, id };
  }

  /** Delete a user → POST /ve/user { id, del:1 } (gated; PERMANENT hard delete — disable is the reversible step). */
  async deleteUser(id, opts = {}) {
    const res = await this._deviceCall('user', { method: 'post', data: { id, del: 1 }, token: opts.token });
    return { ok: !!(res && res.data && res.data.success) };
  }

  /**
   * Enable/disable a user WITHOUT deleting → POST /ve/user/disable { id, fDisabled:0|1 } (gated).
   * Disabling blocks login (auth.js) and hides the user into the "Disabled" list; the reversible
   * step before a permanent hard delete. Authority mirrors resetUserPassword (master, or a non-master
   * who administers a device the target can access; cannot disable self). Returns {ok}.
   * @param {number} id
   * @param {boolean|number} fDisabled - truthy = disable, falsy = re-enable
   * @returns {Promise<{ok:boolean}>}
   */
  async setUserDisabled(id, fDisabled, opts = {}) {
    const res = await this._deviceCall('user/disable', {
      method: 'post',
      data: { id, fDisabled: fDisabled ? 1 : 0 },
      token: opts.token,
    });
    return { ok: !!(res && res.data && res.data.success) };
  }

  /** Reset a user's password → POST /ve/resetpassword { id } (gated; backend generates + emails it). */
  async resetUserPassword(id, opts = {}) {
    const res = await this._deviceCall('resetpassword', { method: 'post', data: { id }, token: opts.token });
    return { ok: !!(res && res.data && res.data.success) };
  }

  // ---- setup: org (customers / sites / devices) --------------------------
  // The cloud org hierarchy (customer ▸ site ▸ device) that isite's admin drives, over the same
  // /ve endpoints. Reads are trusted-token-scoped server-side (a master sees all; a customer-admin
  // sees only their own — admin.js getCustomers/getProjects/getDevices). Writes are Phase-1 gated
  // (modifyOrCreate{Customer,Project,Device}: customer = master-only, site/device = admin + org
  // scope). NOTE (device write): modifyOrCreateDevice ALWAYS calls modifyDeviceConfig, which
  // ini.stringifies body.config into JSDeviceConfig.sConfig — so an org-assignment save MUST
  // round-trip the device's CURRENT parsed config or it is wiped. saveDevice carries it, exactly as
  // isite's DeviceForm does (proven path). Config shape differs by family (dragon [camN] + rich
  // top-level [global]/[server]/… vs legacy [cameraN] + [libaiengine]); we never reshape it —
  // parse→stringify round-trips whatever the device pushed.

  /**
   * GET /ve/customers → { customers } (JSCustomer rows: bID/sName/sDir/sDomain/fPublic). Empty-safe.
   * @returns {Promise<{customers:Array}>}
   */
  async getCustomers(opts = {}) {
    const res = await this._deviceCall('customers', { params: { id: this._selfId() }, token: opts.token });
    const d = (res && res.data) || {};
    return { customers: d.customers || [] };
  }

  /**
   * Create/update a customer → POST /ve/customer (master-only, gated). Body {id?, name, dir,
   * domain, public}; omit id to create. Returns {ok,id} (INSERT → data[0]).
   * @returns {Promise<{ok:boolean,id:?number}>}
   */
  async saveCustomer(c = {}, opts = {}) {
    const body = { name: c.name, dir: c.dir, domain: c.domain, public: c.public ? 1 : 0 };
    if (c.id != null) body.id = c.id;
    const res = await this._deviceCall('customer', { method: 'post', data: body, token: opts.token });
    const ok = !!(res && res.data && res.data.success);
    const data = res && res.data && res.data.data;
    const id = c.id != null ? c.id : (Array.isArray(data) ? data[0] : data);
    return { ok, id };
  }

  /** Delete a customer → POST /ve/customer { id, del:1 } (master-only; reversible SOFT delete —
   *  backend flips JSCustomer.fDeleted, hiding the customer + its subtree from every read). */
  async deleteCustomer(id, opts = {}) {
    const res = await this._deviceCall('customer', { method: 'post', data: { id, del: 1 }, token: opts.token });
    return { ok: !!(res && res.data && res.data.success) };
  }

  /**
   * GET /ve/projects → { customers, projects } ("project" is the DB name for a site; each row is a
   * JSCustomerProject + sCustomerName + url). The customers list feeds the site's Customer picker.
   * Empty-safe.
   * @returns {Promise<{customers:Array,projects:Array}>}
   */
  async getProjects(opts = {}) {
    const res = await this._deviceCall('projects', { params: { id: this._selfId() }, token: opts.token });
    const d = (res && res.data) || {};
    return { customers: d.customers || [], projects: d.projects || [] };
  }

  /**
   * Create/update a site → POST /ve/project (admin + org-scope gated). Body {id?, name, dir,
   * customerId, dataPlan, timezone, reportErrors}; dir mirrors the parent customer's sDir. Returns
   * {ok,id}.
   * @returns {Promise<{ok:boolean,id:?number}>}
   */
  async saveProject(p = {}, opts = {}) {
    const body = {
      name: p.name,
      dir: p.dir || '',
      customerId: p.customerId,
      dataPlan: p.dataPlan != null ? p.dataPlan : 0,
      timezone: p.timezone || '',
      reportErrors: p.reportErrors ? 1 : 0,
    };
    if (p.id != null) body.id = p.id;
    const res = await this._deviceCall('project', { method: 'post', data: body, token: opts.token });
    const ok = !!(res && res.data && res.data.success);
    const data = res && res.data && res.data.data;
    const id = p.id != null ? p.id : (Array.isArray(data) ? data[0] : data);
    const error = !ok && res && res.data && typeof res.data.error === 'string' ? res.data.error : undefined;
    return { ok, id, error };
  }

  /** Delete a site → POST /ve/project { id, del:1 } (gated; unassigns devices + drops JSAcl). */
  async deleteProject(id, opts = {}) {
    const res = await this._deviceCall('project', { method: 'post', data: { id, del: 1 }, token: opts.token });
    return { ok: !!(res && res.data && res.data.success) };
  }

  /**
   * GET /ve/devices → { customers, projects, devices }. Each device: bID/sDeviceID/sCurrentIP/
   * sLastEventTS/convertedTS/bCameras/sName/fOnline/sBackendID + PARSED sConfig + org labels
   * (bCustomerID/sCustomerName/bCustomerProjectID/sProjectName). Empty-safe.
   * @returns {Promise<{customers:Array,projects:Array,devices:Array}>}
   */
  async getDevices(opts = {}) {
    const res = await this._deviceCall('devices', { params: { id: this._selfId() }, token: opts.token });
    const d = (res && res.data) || {};
    return { customers: d.customers || [], projects: d.projects || [], devices: d.devices || [] };
  }

  /**
   * Update a device's ORG ASSIGNMENT (name + customer + site) → POST /ve/device (admin + org-scope
   * gated). Devices self-register, so this is UPDATE-only (no create/delete). The caller MUST pass
   * the device's CURRENT parsed config so modifyDeviceConfig round-trips it (see the section note).
   * Distinct from setDeviceConfig (config-only, identity sourced from the session) — saveDevice is
   * the identity-CHANGING sibling.
   * @param {{id:number, name:string, customerId:number, projectId:number, deviceId:string, config:Object}} dev
   * @returns {Promise<{ok:boolean}>}
   */
  async saveDevice(dev = {}, opts = {}) {
    const body = {
      id: dev.id,
      name: dev.name != null ? dev.name : '',
      customerId: dev.customerId,
      projectId: dev.projectId,
      deviceId: dev.deviceId,
      config: dev.config || {},
    };
    // David 2026-08-21 (#86bbfuajj): the frontend save is LIVE — POST /ve/device via veCall. The backend
    // persists the full device record (JSDevice org assignment + JSCamera + JSDeviceConfig via
    // modifyDeviceConfig).
    const res = await this._deviceCall('device', { method: 'post', params: { device: dev.deviceId }, data: body, token: opts.token });
    const ok = !!(res && res.data && res.data.success);
    const error = !ok && res && res.data && typeof res.data.error === 'string' ? res.data.error : undefined;
    return { ok, error };
  }

  // ---- setup: cameras + device config (admin) ----------------------------
  // Cloud camera administration over /ve. The Cameras screen edits the device's INI config blob
  // (JSDeviceConfig.sConfig) + the friendly camera name (JSCamera.sName). Reads come from
  // GET /ve/cameras (each camera row carries the PARSED device config — see the backend asymmetry
  // note on getDeviceConfig); writes ride POST /ve/camera (Phase-1 gated: bRole 1/2 + device scope).
  // Discovery stages new camera config sections. JSCamera rows can register after that config exists.

  /**
   * GET /ve/cameras → { cameras, devices, customers, projects }. Each CAMERA row carries the
   * PARSED device config (`sConfig`), its `sDeviceID/bCamera/bProfile`, the DB friendly name
   * `sName`, and org labels. Device rows also carry parsed `sConfig`, so configured cameras remain
   * visible before their JSCamera rows register.
   * Empty-safe.
   * @returns {Promise<{cameras:Array,devices:Array,customers:Array,projects:Array}>}
   */
  async getCameras(opts = {}) {
    const res = await this._deviceCall('cameras', {
      params: { id: this._selfId() },
      token: opts.token,
    });
    const d = (res && res.data) || {};
    return {
      cameras: d.cameras || [],
      devices: d.devices || [],
      customers: d.customers || [],
      projects: d.projects || [],
    };
  }

  /**
   * The PARSED device config (INI blob) for one device. Reads it off a camera row from
   * GET /ve/cameras (those carry parsed config); returns {} if the device has no cameras / config.
   * A convenience read seam over getCameras — the Cameras screen loads everything once via
   * getCameras and holds a working copy, so this is mainly for callers that want one device's config.
   * @param {string} deviceId
   * @returns {Promise<Object>} parsed config object (may be {})
   */
  async getDeviceConfig(deviceId, opts = {}) {
    const { cameras } = await this.getCameras(opts);
    const row = (cameras || []).find((c) => c && c.sDeviceID === deviceId && c.sConfig && typeof c.sConfig === 'object');
    return (row && row.sConfig) || {};
  }

  /**
   * Write a device's FULL config blob → POST /ve/device (gated). The backend's modifyDeviceConfig
   * ini.stringifies `body.config` back into JSDeviceConfig.sConfig, and modifyOrCreateDevice also
   * UPDATEs sName/bCustomerID/bCustomerProjectID — so identity MUST be sent or it would be nulled.
   * We source identity from the in-memory session device record (never client-chosen) and throw if
   * the device isn't in the session, rather than blind-writing and clobbering its org assignment.
   * This is the device-LEVEL seam (top-level [global]/[server]/… sections); camera edits use
   * saveCamera (POST /ve/camera), which never touches device identity columns.
   * @param {string} deviceId
   * @param {Object} config - the full parsed config to persist
   * @returns {Promise<{ok:boolean}>}
   */
  async setDeviceConfig(deviceId, config, opts = {}) {
    const dev = this._session && this._session.devicesById && this._session.devicesById[deviceId];
    if (!dev) throw new Error(`setDeviceConfig: unknown device ${deviceId} (identity not in session)`);
    const body = {
      id: dev.bID,
      name: dev.name,
      customerId: dev.customerId,
      projectId: dev.projectId,
      deviceId,
      config,
    };
    const res = await veCall('device', { method: 'post', params: { device: deviceId }, data: body, token: opts.token || this._token });
    return { ok: !!(res && res.data && res.data.success) };
  }

  /**
   * Save one camera → POST /ve/camera (gated). The backend's modifyOrCreateCamera does
   * `UPDATE JSCamera SET sName=? WHERE sDeviceID=? AND bCamera=?` (the friendly, cloud-only name —
   * it never propagates to the edge device) AND modifyDeviceConfig replaces the whole sConfig with
   * `body.config`. So callers pass the FULL device config with the one camera's section patched
   * (configParser.writeCamera preserves every other key/section). It does NOT touch device
   * identity columns, so it's the safe camera-edit path.
   * @param {{deviceId:string, bCamera:number, name:string, config:Object}} cam
   * @returns {Promise<{ok:boolean}>}
   */
  async saveCamera(cam = {}, opts = {}) {
    const body = {
      name: cam.name != null ? cam.name : '',
      deviceId: cam.deviceId,
      bCamera: cam.bCamera,
      config: cam.config || {},
    };
    const res = await veCall('camera', { method: 'post', params: { device: cam.deviceId }, data: body, token: opts.token || this._token });
    return { ok: !!(res && res.data && res.data.success) };
  }

  /**
   * Read a Dragon device's ONVIF camera-discovery cache → GET /ve/device/:id/discover.
   * The backend relays to the device and waits up to ~10s, so this uses a longer timeout.
   * cloudapi scrubs credentials (no camera passwords in the payload; rtsp urls retained for
   * add). Returns a branchable result; never throws (an offline/never_scanned device is a
   * normal outcome, not an error). `status` ∈ ok | offline | never_scanned | error.
   * @param {string} deviceId sDeviceID (12 hex)
   * @returns {Promise<{ok:boolean, status:string, timestamp:?string, cameras:Array, message?:string}>}
   */
  async discoverCameras(deviceId, opts = {}) {
    try {
      const res = await veCall('device/discover', {
        params: { device: deviceId }, token: opts.token || this._token, timeout: 15000, signal: opts.signal,
      });
      const d = (res && res.data) || {};
      return { ok: !!d.success, status: d.status || (d.success ? 'ok' : 'error'), timestamp: d.timestamp || null, cameras: Array.isArray(d.cameras) ? d.cameras : [] };
    } catch (e) {
      return { ok: false, status: 'error', timestamp: null, cameras: [], message: cloudErr(e) };
    }
  }

  /**
   * Trigger a fresh network scan on the device → POST /ve/device/:id/discover/scan.
   * Fire-and-forget on the device (30s–2min); poll discoverCameras() afterward and compare the
   * timestamp to detect completion. Returns { ok, queued }.
   * @param {string} deviceId
   * @returns {Promise<{ok:boolean, status:string, queued:boolean, message?:string}>}
   */
  async rescanCameras(deviceId, opts = {}) {
    try {
      const res = await veCall('device/discover/scan', {
        method: 'post', params: { device: deviceId }, data: {}, token: opts.token || this._token, signal: opts.signal,
      });
      const d = (res && res.data) || {};
      return { ok: !!d.success, status: d.status || (d.success ? 'ok' : 'error'), queued: !!d.queued };
    } catch (e) {
      return { ok: false, status: 'error', queued: false, message: cloudErr(e) };
    }
  }

  /**
   * One camera's live detail → GET /ve/device/:id/discover/cam?ip=&action=.
   * Waits up to ~30s on the device, so a longer timeout. Default action 'query'
   * returns { camera }; ONVIF ops (default|reset|reboot) return { result } — reset/reboot are
   * master-gated server-side.
   * @param {string} deviceId
   * @param {string} ip dotted-quad
   * @param {string} [action='query']
   * @returns {Promise<{ok:boolean, status:string, camera:?Object, result:?Object, message?:string}>}
   */
  async discoverCamera(deviceId, ip, action = 'query', opts = {}) {
    try {
      const res = await veCall('device/discover/cam', {
        ...(action === 'query' ? {} : { method: 'post', data: {} }),
        params: { device: deviceId, ip, action }, token: opts.token || this._token, timeout: 35000, signal: opts.signal,
      });
      const d = (res && res.data) || {};
      return { ok: !!d.success, status: d.status || (d.success ? 'ok' : 'error'), camera: d.camera || null, result: d.result || null };
    } catch (e) {
      return { ok: false, status: 'error', camera: null, result: null, message: cloudErr(e) };
    }
  }

  // ---- setup: ScaleWatcher enrollment (#86bc6dkfa, cloudapi ADR-025) -------
  // Dividia-master only: cloudapi's authDevicesDividiaMaster re-reads the caller's role on every call.
  // Both resolve (never throw) with the server's own refusal text, so the dialog can show it.

  /**
   * The Add-to-ScaleWatcher dialog's data → GET /ve/sw/parents?device=. The ScaleWatcher customers a
   * site can go under, plus THIS device's derived serial, proposed site name, eligibility (with the
   * reason when not), and its existing site when already enrolled.
   * @param {string} deviceId sDeviceID
   * @returns {Promise<{ok:boolean, customers:Array, customerTypes:Array, defaultCustomerType:string,
   *   ticketProvider:string, device:?Object, status?:number, error?:string}>}
   */
  async getScalewatcherEnrollment(deviceId, opts = {}) {
    try {
      const res = await veCall('sw/parents', { params: { device: deviceId }, token: opts.token || this._token });
      return { ok: true, ...((res && res.data) || {}) };
    } catch (e) {
      return {
        ok: false, status: e && e.response && e.response.status, customers: [], device: null,
        error: swEnrollmentError(e, 'Could not check ScaleWatcher.'),
      };
    }
  }

  /**
   * Add the device to ScaleWatcher → POST /ve/sw/enroll?device=  body { parentId } | { newCustomerName },
   * plus customerType. Idempotent server-side: an already-enrolled device returns its site with
   * alreadyEnrolled:true. A refusal carries the server's `code` (customer_exists, serial_in_use, …)
   * and its extra fields (e.g. customerId) in `details`.
   * @param {string} deviceId sDeviceID
   * @param {{parentId?:number, newCustomerName?:string, customerType?:string}} body
   * @returns {Promise<{ok:boolean, result?:Object, status?:number, error?:string, code?:string, details?:Object}>}
   */
  async enrollInScalewatcher(deviceId, body, opts = {}) {
    try {
      const res = await veCall('sw/enroll', {
        method: 'post', params: { device: deviceId }, data: body, token: opts.token || this._token, timeout: 20000,
      });
      return { ok: true, result: (res && res.data) || {} };
    } catch (e) {
      const d = e && e.response && e.response.data;
      return {
        ok: false, status: e && e.response && e.response.status,
        error: swEnrollmentError(e, 'Could not add this device to ScaleWatcher.'),
        code: d && d.code, details: d || null,
      };
    }
  }

  /**
   * PTZ presets for a device camera → GET /ve/getptzpresets/:camera?device= (JSPtzPreset rows:
   * bPresetNum/sName). Returns [] if the device has none. NOTE: these endpoints manage preset
   * NAME↔number entries only — there is no "recall/move-to-preset" control endpoint yet, so v1 is
   * preset management (recall arrives with live PTZ control later).
   * @param {string} deviceId
   * @param {number} cam
   * @returns {Promise<Array<{bPresetNum:number, sName:string}>>}
   */
  async getPtzPresets(deviceId, cam, opts = {}) {
    const res = await veCall(`getptzpresets/${cam}`, { params: { device: deviceId }, token: opts.token || this._token });
    const d = res && res.data;
    return Array.isArray(d) ? d : [];
  }

  /**
   * Create or rename a PTZ preset → POST /ve/setptzpreset?device= { camera, presetNum, name }.
   * @returns {Promise<{ok:boolean}>}
   */
  async savePtzPreset(deviceId, cam, presetNum, name, opts = {}) {
    const res = await veCall('setptzpreset', {
      method: 'post',
      params: { device: deviceId },
      data: { camera: cam, presetNum, name },
      token: opts.token || this._token,
    });
    return { ok: !!(res && res.data && !res.data.error) };
  }

  /**
   * Remove a PTZ preset → GET /ve/removeptzpreset/:camera/:preset?device=.
   * @returns {Promise<{ok:boolean}>}
   */
  async removePtzPreset(deviceId, cam, presetNum, opts = {}) {
    const res = await veCall(`removeptzpreset/${cam}/${presetNum}`, { params: { device: deviceId }, token: opts.token || this._token });
    return { ok: !!(res && res.data && !res.data.error) };
  }

  // ---- identity / capability --------------------------------------------

  /**
   * Cloud feature flags. Unlike the local NVR there is no per-server getStartup;
   * the cloud always speaks WebSocket and is not eView. Shape matches
   * LocalNvrAdapter.getCapabilities so consumers are uniform.
   * @returns {Promise<{hasWebSocket:boolean,hasEView:boolean,features:string[],backendVersion:*}>}
   */
  async getCapabilities() {
    return {
      hasWebSocket: true,
      hasEView: false,
      features: ['websocket', 'cloud'],
      backendVersion: this._session ? this._session.backendVersion : undefined,
    };
  }

  /**
   * Capability descriptor for a device (by id or record). This is the ONE place
   * the viewer asks dragon-vs-regular; the playback/config builders branch on it
   * (the live player no longer does).
   * @param {string|Object} deviceOrId
   * @returns {{directMedia:boolean,proxiedMedia:boolean,richConfig:boolean}}
   */
  getDeviceCapabilities(deviceOrId) {
    if (typeof deviceOrId === 'string') {
      const d = this._session && this._session.devicesById[deviceOrId];
      if (!d) return classifyDevice({ sDeviceID: deviceOrId });
      if (d.isModern) return { directMedia: true, proxiedMedia: false, richConfig: true };
      return classifyDevice(d.raw);
    }
    return classifyDevice(deviceOrId || {});
  }

  // ---- stream URL builders (Seam B — capability-aware) -------------------
  // A camera is identified by (deviceId, cam#, profile). Accepts a record with
  // sDeviceID/deviceId + bCamera/cam + bProfile/profile, so a grid camera entry
  // can be passed straight through.

  _resolve(camera = {}) {
    return {
      deviceId: camera.sDeviceID || camera.deviceId,
      cam: camera.bCamera ?? camera.cam,
      profile: camera.bProfile ?? camera.profile ?? 1,
    };
  }

  /**
   * Live stream URL for a cloud camera — ONE path for every device (dragon and
   * legacy alike): a raw-H.264 WebSocket off engine_backend, feeding the WebCodecs
   * PlayerWS. The JWT rides as a `?token=` query param because a WebSocket can't
   * carry an Authorization header; prod cloudapi validates+strips it, and the
   * dev-dragon engine_backend tolerates (ignores) it, so appending it is always
   * safe. (When engine_backend moves the token to a Sec- header, change it here.)
   * @param {Object} camera - { sDeviceID/deviceId, bCamera/cam, bProfile/profile }
   * @param {number} [profileOverride]
   * @param {Object} [_ctx] - local-only ctx (eView auth); accepted for a uniform
   *   call site, ignored by cloud (the JWT + device index live on the instance).
   * @returns {string} the live WS URL (with the token appended when present)
   */
  buildLiveUrl(camera, profileOverride, _ctx) {
    const { deviceId, cam, profile } = this._resolve(camera);
    const p = profileOverride ?? profile;
    const url = buildCloudLiveUrl(deviceId, cam, p);
    return this._token ? `${url}?token=${encodeURIComponent(this._token)}` : url;
  }

  // Hosted cloud builds route Dragon media through CloudAPI. Browser media
  // fetches cannot attach a Bearer header consistently, so carry the same cloud
  // JWT as a redacted query parameter. Fail closed for unknown or insecure
  // origins so a bad build setting cannot disclose the session credential.
  _authorizeMediaUrl(url) {
    return this._token && canAttachCloudToken(url)
      ? `${url}?auth=${encodeURIComponent(this._token)}`
      : url;
  }

  /**
   * Recorded-clip URL. DRAGON -> a byte-range MP4 from the configured media origin (feeds
   * PlaybackPlayer). REGULAR → the pbctrl-cached flow (trigger + poll + fetch)
   * lands Day-3; not a bare URL, so it throws the contract guard here.
   * @param {Object} clip - { sDeviceID/deviceId, bCamera/cam, ts (14-digit) }
   * @returns {string} MP4 URL (dragon)
   */
  buildClipUrl(clip = {}) {
    const { deviceId, cam } = this._resolve(clip);
    const ts = clip.ts || clip.sTimeStamp || clip._utcTs;
    if (this.getDeviceCapabilities(deviceId).directMedia) {
      return this._authorizeMediaUrl(buildCloudClipUrl(deviceId, cam, ts));
    }
    // REGULAR field-fleet: recorded clip on the cloud-uploads host, keyed by
    // device/cam/UTC-ts (Java sCloudUrl + /uploads + file). ts must be the UTC stamp.
    const proj = this.getDeviceProject(deviceId);
    return proj ? buildCloudLegacyClipUrl(proj, deviceId, cam, ts) : '';
  }

  // ---- recorded playback (Day-3) ----------------------------------------
  // Reuses the dview Java cloud-playback flow (EventModel + CloudModel +
  // MCPlayerController): list events per camera over a window, resolve each clip's
  // cloud-storage URL, and (for uncached clips) trigger the pbctrl pull + poll.

  /** The JSCustomerProject record for a device (cloud-storage url + timezone), or null. */
  getDeviceProject(deviceId) {
    if (!this._session) return null;
    const dev = this._session.devicesById[deviceId];
    if (!dev) return null;
    const byId = this._session.projectsById || {};
    return byId[dev.projectId] || null;
  }

  /**
   * The site timezone that drives the single header clock + playback "now" in cloud (#86bbm5p92).
   * Phase 1 is one-zone-per-session, keyed on the ACTIVE device's project: its zone, or '' (browser-
   * local) when that site has none set. It must NOT borrow another site's zone once a device is active,
   * or the clock/label and the dragon playback window would read a zone the viewed site does not use,
   * returning shifted/empty results (#86bbm5p92 review L2). Only with NO active project (e.g. right
   * after login, before a device is selected) does it fall back to the first site that carries a zone.
   * Per-camera zone still resolves via getDeviceProject(deviceId) — this is only the one shared clock.
   * @returns {string} IANA zone (e.g. 'America/Chicago') or '' when the active site has none
   */
  getPrimaryTimezone() {
    const active = this.getDeviceProject(this.getActiveDeviceId());
    if (active) return active.sTimezone || '';   // active site drives the clock; unset -> browser-local, never another site's zone
    const byId = (this._session && this._session.projectsById) || {};
    for (const id of Object.keys(byId)) {
      if (byId[id] && byId[id].sTimezone) return byId[id].sTimezone;
    }
    return '';
  }

  /**
   * Reflect a Setup timezone save in the cached session so the header clock + playback "now" update
   * WITHOUT a re-login (#86bbm5p92). Mutates the in-memory project + re-persists so a refresh keeps
   * it (the cached login payload otherwise holds the pre-save zone until the next full login). The
   * caller then re-seeds Redux via SET_NVR_TIMEZONE(getPrimaryTimezone()).
   * @param {number|string} projectId - JSCustomerProject bID
   * @param {string} tz - IANA zone
   */
  updateProjectTimezone(projectId, tz) {
    const byId = (this._session && this._session.projectsById) || {};
    const proj = byId[projectId];
    if (proj) { proj.sTimezone = tz || ''; this._persist(); }
  }

  /**
   * Map one raw `/ve/playbackevents?raw=1` event to the MCPlayback engine's clip
   * shape (util/mcpbClips): the engine reads `sTimestamp` (clip START) + `bLength`
   * (duration). Timestamp zone is DEVICE-FAMILY-AWARE (#86bbm5p92): DRAGON writes
   * device-LOCAL wall-clock to the cloud (verified: dragon camera_thread.c/record_thread.c
   * use localtime_r, no gmtime) and the site's JSCustomerProject.sTimezone IS that local
   * zone, so `sTimestamp` passes through UNSHIFTED; LEGACY cloud devices store true UTC
   * (isite engine localtime=0) so `sTimestamp` is converted UTC->site-local. The ORIGINAL
   * stored ts is kept (`_utcTs`) for the clip URL + cache trigger. `sMovie` is the fully-
   * resolved cloud MP4 URL, played straight by the <video> (buildStreamUrl http-
   * passthrough); `fCloudVS` marks it.
   *
   * Clip URL is CAPABILITY-based (the one dragon-vs-regular split, matching live):
   *   - DRAGON (directMedia): a byte-range MP4 from the configured media origin,
   *     keyed by device/cam/UTC-ts. Hosted builds use the CloudAPI auth proxy.
   *   - REGULAR field fleet: the project's cloud-storage clip (Java getRecordEventURL),
   *     which must be pulled to storage first (ensureClipCached: /ve/videoevent + poll).
   */
  _mapPlaybackEvent(ev, deviceId, cam, project, directMedia) {
    if (!ev || !ev.sTimeStamp) return null;
    const utcTs = ev.sTimeStamp;
    const tz = project && project.sTimezone;
    // Dragon records the LIVE profile as a twin next to each main clip (<ts>.pro2.mp4) — lighter
    // to decode. Expose BOTH profile URLs; the multi-cam VIEW chooses per tile by layout (big tile
    // = pro1 for detail, small grid tiles = pro2 for lower decode load), then the player retries the
    // main clip on engine_backend's fast 404 for a clip with no record_pro2 twin — per-tile, NO
    // device-config read. `sMovie` stays the full main clip (also the download/export source);
    // legacy field-fleet clips have no twin, so `sMoviePro2` stays ''.
    const sMovie = directMedia
      ? this._authorizeMediaUrl(buildCloudClipUrl(deviceId, cam, utcTs, false)) // dragon: main clip (pro1)
      : (project ? buildCloudLegacyClipUrl(project, deviceId, cam, utcTs) : '');
    const sMoviePro2 = directMedia
      ? this._authorizeMediaUrl(buildCloudClipUrl(deviceId, cam, utcTs, true))  // dragon: LIVE-profile twin (pro2)
      : '';
    return {
      bID: ev.bID,
      bCamera: ev.bCamera != null ? ev.bCamera : Number(cam),
      bSerial: 0,                    // cloud has no NVR serial — keeps the trackEvents key uniform
      sDeviceID: deviceId,
      // Dragon digits are already site-local wall-clock; only legacy cloud is UTC. See docstring.
      sTimestamp: directMedia ? utcTs : utcToLocal(utcTs, tz),
      bLength: Number(ev.bDuration) || 0,
      sMovie,
      sMoviePro2,                    // LIVE-profile twin URL for multi-cam tiles; player falls back to sMovie on 404 (dragon only; '' otherwise)
      fCloudVS: 1,
      directMedia: !!directMedia,    // dragon clips are directly seekable; regular need caching
      sTags: ev.sTags || '',
      // Cloud JSEvent has ONE flag column, `fFlagged`, used as a BITMASK (1=archive, 2=export, 3=both) —
      // there is no separate fArchive/fExport on the cloud side. Derive both engine flags from it so the
      // MCPlayback Archive/Export status + the setEventFlag optimistic flip work uniformly with local.
      fArchive: (Number(ev.fFlagged) & 1) ? 1 : 0,
      fExport: (Number(ev.fFlagged) & 2) ? 1 : 0,
      // Dragon MP4 is served on demand (no cache step); treat as ready. Regular uses the flag.
      fCached: directMedia ? 1 : (ev.fCached ? 1 : 0),
      dCacheProgress: Number(ev.dCacheProgress) || 0,
      _utcTs: utcTs,
    };
  }

  /**
   * Recorded events for ONE camera over a device-local [startLocalTs, endLocalTs]
   * window (both 'YYYYMMDDHHMMSS'). Converts the window to UTC for the query and each
   * event back to device-local. Returns engine-shaped clips (may be []).
   * @returns {Promise<Array<Object>>}
   */
  async fetchPlaybackEvents(deviceId, cam, startLocalTs, endLocalTs) {
    const project = this.getDeviceProject(deviceId);
    const tz = project && project.sTimezone;
    const directMedia = this.getDeviceCapabilities(deviceId).directMedia;
    // Family-aware window (#86bbm5p92): dragon stores device-LOCAL wall-clock (= the site zone), so its
    // window passes through UNSHIFTED — the raw cloudapi path string-matches stored digits, and
    // localToUtc-shifting a dragon window returns ZERO rows (verified on web1: America/Chicago site's
    // 16:xx-local events missed by a 21:xx-UTC window). Legacy cloud stores true UTC → keep the round-trip.
    // PHASE-1 ASSUMPTION (one-zone-per-view, see docs §6): the caller's window is the single primary-site
    // "now" (getPrimaryTimezone). A cross-device view whose devices span DIFFERENT project zones would need
    // the window re-derived per device's own sTimezone — deferred to the Phase-2 per-tile work.
    const start = directMedia ? startLocalTs : localToUtc(startLocalTs, tz);
    const end = directMedia ? endLocalTs : localToUtc(endLocalTs, tz);
    const res = await veCall('playbackevents', {
      params: { raw: 1, device: deviceId, cam, start, end },
      token: this._token,
    });
    const body = res && res.data;
    const rows = (body && (body.result || (Array.isArray(body) ? body : []))) || [];
    return rows.map((ev) => this._mapPlaybackEvent(ev, deviceId, cam, project, directMedia)).filter(Boolean);
  }

  /**
   * Recording-day PRESENCE for ONE camera over a device-local month window: which days have footage,
   * for the MCPlayback calendar dots (card 86bbqvr6f). Returns a boolean[32] (day 1..31; index 0
   * unused) that the caller OR-merges across the view's cameras. This is the CLOUD replacement for the
   * local NVR's `event.getEventPresence` JSON-RPC: it goes over `/ve` (REST) via veCall, so it can NEVER
   * produce the JSON-RPC `noauth` the session interceptor watches — which is exactly the false
   * "Session Expired" this card fixes (the old path POSTed to a local NVR `/JSON/`, which noauths in
   * cloud). A real JWT-expiry surfaces as a normal 401 rejection, handled by the cloud session, not here.
   *
   * Family-aware window, EXACTLY like fetchPlaybackEvents: DRAGON stores device-LOCAL wall-clock, so its
   * window passes UNSHIFTED and the endpoint buckets by the stored digits (`localstored=1`); LEGACY
   * stores UTC, so the window is localToUtc-shifted and the endpoint buckets by CONVERT_TZ(...,tz).
   *
   * Cached per (device, cam, YYYYMM): paging months in a fixed view is free, and a view/grid change only
   * fetches the newly-added cameras — never re-fetches the rest, never other systems. THROWS on a
   * transient failure (network/404/500) so the caller drops just this camera (empty dots, retried next
   * open) rather than caching a bad month.
   *
   * @param {string} deviceId
   * @param {number|string} cam
   * @param {string} startLocalTs  device-local 'YYYYMMDDHHMMSS' month start
   * @param {string} endLocalTs    device-local 'YYYYMMDDHHMMSS' month end
   * @param {{year:number, month0:number}} month  the visible month (folds day buckets to day-of-month)
   * @returns {Promise<boolean[]>}  day dots, length 32
   */
  async fetchEventPresence(deviceId, cam, startLocalTs, endLocalTs, { year, month0 } = {}) {
    const cacheKey = `${deviceId}|${cam}|${monthKey(year, month0)}`;
    if (this._presenceCache[cacheKey]) return this._presenceCache[cacheKey];

    const project = this.getDeviceProject(deviceId);
    const tz = (project && project.sTimezone) || '';
    const directMedia = this.getDeviceCapabilities(deviceId).directMedia;
    // Family-aware window (see fetchPlaybackEvents): dragon local passes through, legacy shifts to UTC.
    const start = directMedia ? startLocalTs : localToUtc(startLocalTs, tz);
    const end = directMedia ? endLocalTs : localToUtc(endLocalTs, tz);

    // LEGACY buckets by site-local day server-side via an OFFSET-based CONVERT_TZ (the cloud RDS has no
    // named-timezone tables — see tzOffsetString). Dragon uses the LEFT(stored) path, so its offset is moot.
    const res = await veCall('eventpresence', {
      params: {
        device: deviceId, cam, start, end,
        localstored: directMedia ? 1 : 0,
        tzoffset: directMedia ? '+00:00' : tzOffsetString(tz, startLocalTs),
      },
      token: this._token,
    });
    // { result: { 'YYYYMMDD': count } } for this one camera → fold to day-of-month dots for (year, month0).
    const map = (res && res.data && res.data.result) || {};
    const dots = dayDots({ [cam]: map }, year, month0);
    this._presenceCache[cacheKey] = dots;
    return dots;
  }

  /**
   * Ensure a recorded clip is cached on cloud storage before it is playable. Regular
   * field-fleet clips are pulled from the device on demand (Java requestVideoEvent +
   * getVideoEventCacheStatus): trigger `/ve/videoevent`, then poll
   * `/ve/videoeventdownloadstatus` until dCacheProgress hits 100. No-op for a clip
   * already cached. Reports progress via onProgress and resolves {ok, error}.
   *
   * Auth: the status poll is JWT-guarded (passport.authDevices), so it needs a valid
   * token. Callers pass the cloud JWT from Redux (opts.token = state.auth.sSess) — the
   * reliable source — since the adapter's in-memory _token can be lost on a code reload.
   * Errors are surfaced (not swallowed into an endless spinner), mirroring Java
   * MCPlayerController.downloadToFile which shows "HTTP 401" / "Timed out".
   *
   * Timeout is IDLE-based, not total: a legacy pull is the device uploading the clip on
   * demand over its own (often slow) uplink, so we keep waiting as long as dCacheProgress
   * is still CLIMBING and only give up after `idleTimeoutMs` of NO progress (a wedged
   * pull), bounded by a generous `maxTotalMs`. The storage serves the clip only once the
   * pull is COMPLETE (it 404s mid-write — verified), so progressive/head play isn't
   * possible; the clip becomes playable at 100%.
   * @param {Object} clip - a _mapPlaybackEvent() result
   * @param {{onProgress?:function(number):void, idleTimeoutMs?:number, maxTotalMs?:number, token?:string}} [opts]
   * @returns {Promise<{ok:boolean, error:?string}>}
   */
  async ensureClipCached(clip, opts = {}) {
    if (!clip || clip.fCached) return { ok: true, error: null };
    const { onProgress, idleTimeoutMs = 30000, maxTotalMs = 600000, token } = opts;
    const auth = token || this._token;
    try {
      await veCall('videoevent', {
        params: { device: clip.sDeviceID, cam: clip.bCamera, ts: clip._utcTs },
        token: auth,
      });
    } catch (e) { /* trigger is best-effort; the poll below reports real status */ }

    let lastProg = -1;
    let lastAdvance = Date.now();          // reset whenever progress climbs (idle-timeout basis)
    const hardDeadline = Date.now() + maxTotalMs;
    // eslint-disable-next-line no-constant-condition
    while (true) {
      let prog = lastProg < 0 ? 0 : lastProg;
      try {
        const r = await veCall('videoeventdownloadstatus', {
          params: { device: clip.sDeviceID, id: clip.bID },
          token: auth,
        });
        const result = r && r.data && r.data.result;
        prog = Number(result && result.dCacheProgress) || 0;
      } catch (e) {
        const status = e && e.response && e.response.status;
        // Auth/permission errors won't clear on retry — surface immediately.
        if (status === 401 || status === 403) return { ok: false, error: `HTTP ${status}` };
        // network/timeout/5xx — keep polling (treated as no-progress toward the idle timeout)
      }
      if (onProgress) onProgress(prog);
      if (prog >= 100) return { ok: true, error: null };
      if (prog > lastProg) { lastProg = prog; lastAdvance = Date.now(); } // still pulling → hold the timeout
      const idleMs = Date.now() - lastAdvance;
      if (idleMs >= idleTimeoutMs) return { ok: false, error: 'Stalled' }; // no data arriving → give up
      if (Date.now() >= hardDeadline) return { ok: false, error: 'Timed out' };
      await new Promise((resolve) => setTimeout(resolve, 1000));
    }
  }

  /**
   * Flag / unflag a recorded cloud event for archive or export. The cloud JSEvent has a SINGLE
   * `fFlagged` INTEGER used as a BITMASK (1=archive, 2=export, 3=both) — POST /ve/flagevent with
   * `flag` (1|2) + `value` (0|1) sets/clears just that bit server-side and preserves the other, so
   * the two local-style flags coexist in one column. Same path for DRAGON and LEGACY (both are JSEvent
   * rows; the endpoint is device-agnostic and `passport.authDevices` checks the device claim, so
   * `device` must be sent). The cloud analog of the local JSON-RPC `event.setEventFlag`.
   *
   * Auth: JWT from the caller (opts.token = Redux state.auth.sSess, the reliable source across code
   * reloads) falling back to the adapter's in-memory token — mirrors ensureClipCached.
   * @param {{bID:number, flag:1|2, value:boolean, deviceId:string, token?:string}} opts
   * @returns {Promise<{ok:boolean}>}
   */
  async flagEvent({ bID, flag, value, deviceId, token } = {}) {
    const res = await veCall('flagevent', {
      method: 'post',
      params: { device: deviceId, id: bID, flag, value: value ? 1 : 0 },
      token: token || this._token,
    });
    return { ok: !!(res && res.data && res.data.success) };
  }

  /**
   * Snapshot/thumbnail URL. DRAGON → the engine_backend `.jpg` sibling. REGULAR →
   * the cloudapi `/ve/snapshot` proxy. (The token rides as a query param for
   * <img>/media elements, which can't send an Authorization header.)
   * @param {Object} camera
   * @param {string} [ts] - for dragon recorded thumbnails
   */
  buildResourceUrl(camera, ts) {
    const { deviceId, cam, profile } = this._resolve(camera);
    if (this.getDeviceCapabilities(deviceId).directMedia && ts) {
      return this._authorizeMediaUrl(buildCloudThumbUrl(deviceId, cam, ts));
    }
    const auth = this._token ? `&auth=${encodeURIComponent(this._token)}` : '';
    return `${getCloudApiBase()}/ve/snapshot?device=${deviceId}&cam=${cam}&pro=${profile}${auth}`;
  }

  /**
   * Best-effort frame for the Setup mask / LPR-ROI editors. DRAGON has no on-demand live single-
   * frame JPEG, but a camera's field of view is fixed — so use the most-recent RECORDED frame:
   * look back a few days and take the latest VIDEO event's `.jpg` sibling (fetched direct off
   * engine_backend, CORS-enabled). REGULAR devices keep the existing `/ve/snapshot` proxy path.
   * Returns '' when there is no recent footage / on error → the editors show "No snapshot" and lock.
   * @param {Object} camera - { deviceId, cam }
   * @returns {Promise<string>}
   */
  async latestThumbUrl(camera) {
    const { deviceId, cam } = this._resolve(camera);
    // Regular/legacy: unchanged — the cloudapi snapshot proxy (no recorded-thumb wiring here).
    if (!this.getDeviceCapabilities(deviceId).directMedia) return this.buildResourceUrl(camera);
    try {
      const pad = (n) => String(n).padStart(2, '0');
      const fmt = (d) => `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`;
      const end = new Date();
      const start = new Date(end.getTime() - 7 * 24 * 3600 * 1000); // 7-day lookback absorbs any tz skew
      const clips = await this.fetchPlaybackEvents(deviceId, cam, fmt(start), fmt(end));
      if (!clips || !clips.length) return '';
      const latest = clips[clips.length - 1]; // fetchPlaybackEvents is ORDER BY ts ASC → last = newest
      const ts = latest._utcTs || latest.sTimestamp;
      return ts ? this.buildResourceUrl(camera, ts) : '';
    } catch (e) {
      return '';
    }
  }

  // ---- regular field-fleet live trigger (KEEP intact) --------------------

  /**
   * GET /ve/streamstart — triggers `pbctrl -S` so cloudapi begins serving the
   * proxied stream for a REGULAR field-fleet device (see cloudapi live.js:
   * "initialize camera stream"; this is a LIVE call, distinct from playback's
   * pbctrl -v). Dragon devices don't need it (their WS auto-inits on connect).
   * Surfaced so the field-fleet path stays reachable; wire it in when a regular
   * device's live path is exercised.
   * @param {string} deviceId
   * @param {number} cam
   * @param {number} [profile=1]
   * @returns {Promise<boolean>} true when cloudapi returns OK
   */
  async streamStart(deviceId, cam, profile = 1) {
    const res = await veCall('streamstart', {
      params: { device: deviceId, cam, pro: profile },
      token: this._token,
    });
    return res?.data?.result === 'OK';
  }
}
